<template>
    <div class="calendar-view calendar-container" :class="{ 'with-panel-open': isPanelOpen }">
        <FullCalendar ref="fullCalendar" :options="calendarOptions" />

        <!-- Touchpoints Side Panel -->
        <transition name="slide-panel">
            <div v-if="showTouchpointsPanel" class="touchpoints-side-panel">
                <div class="panel-header">
                    <div class="panel-title">
                        <h5 class="mb-0">{{ formatPanelDate(touchpointsPanelDate) }}</h5>
                    </div>
                    <button type="button" class="btn btn-secondary btn-icon-only-md" @click="handleCloseTouchpointsPanel" aria-label="Close">
                        <img src="/img/icons/avid-icons/x-circle.svg" class="icon" alt="">
                    </button>
                </div>
                <div class="panel-body">
                    <touchpoint-content-card
                        v-for="touchpoint in touchpointsPanelTouchpoints"
                        :key="touchpoint.id"
                        :touchpoint="touchpoint.asset"
                        :is-collapsed="panelCollapsedCards.includes(touchpoint.asset.id)"
                        :is-selected="false"
                        :show-mini-menu="panelMiniMenuOpenFor === touchpoint.asset.id"
                        :is-primary-asset="false"
                        :show-primary-card="false"
                        :show-primary-inputs="false"
                        :touchpoint-status="getTouchpointStatus(touchpoint.asset)"
                        :is-draft="isDraftTouchpoint(touchpoint.asset)"
                        :mini-menu-two-column="true"
                        :editable-launch-date="true"
                        class="panel-touchpoint-card"
                        @toggle="togglePanelTouchpoint"
                        @view-asset-type-info="viewAssetTypeInfoFromPanel"
                        @open-mini-menu="openPanelMiniMenu"
                        @close-mini-menu="closePanelMiniMenu"
                        @edit="editTouchpointFromPanel"
                        @approve="approveTouchpointFromPanel"
                        @deploy="deployTouchpointFromPanel"
                        @duplicate="duplicateTouchpointFromPanel"
                        @review="reviewTouchpointFromPanel"
                        @delete="deleteTouchpointFromPanel"
                        @launch-date-updated="handlePanelLaunchDateUpdate"
                    />
                </div>
            </div>
        </transition>
    </div>

    <!-- Legend -->
    <!-- <div v-if="getUniqueAssetTypes().length > 0" class="calendar-legend mt-4">
        <div class="asset-key d-flex flex-wrap gap-2">
            <div 
                v-for="(assetType, index) in getUniqueAssetTypes()" 
                :key="`${assetType.id}-${dropdownAssetTypeFilterKey}`" 
                class="key-item d-flex align-items-center gap-2"
                :class="{ 'filtered-out': isAssetTypeFiltered(assetType.id) }"
                @click="toggleAssetTypeFilter(assetType.id)"
            > 
                <div class="color-box" :style="{ 'background-color': getAssetColor(assetType) }"></div>
                <span>{{ assetType.name }}</span>
            </div>
        </div>
    </div> -->

    <!-- Edit Touchpoint Modal Component -->
    <edit-touchpoint-modal 
        :asset="selectedAssetForModal" 
        :status="selectedAssetStatus"
        :show="showModal" 
        :concepts="concepts"
        :client="client"
        @edit-asset="handleEditAsset"
        @close="closeModal" />

    <!-- Panel Asset Type Info Modal -->
    <asset-type-info-modal 
        :asset-type="panelSelectedAssetType" 
        :show="panelShowAssetTypeModal" 
        @close="closePanelAssetTypeModal" />
</template>

<script>
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import permissionsMixin from '../../mixins/permissions';
import filtersMixin from '../../mixins/filters';
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import { useNotifications } from '../../composables/useNotifications';
import AssetTypeInfoModal from '../playbooks/AssetTypeInfoModal.vue';
import EditTouchpointModal from '../playbooks/EditTouchpointModal.vue';
import TouchpointContentCard from '../playbooks/TouchpointContentCard.vue';

export default {
    components: {
        FullCalendar,
        AssetTypeInfoModal,
        EditTouchpointModal,
        TouchpointContentCard
    },
    mixins: [permissionsMixin, filtersMixin],
    emits: ['update-days', 'cards-state-changed', 'edit-asset', 'review-single-touchpoint', 'filter-asset-type', 'update-dropdown-filter', 'open-touchpoints-panel', 'close-touchpoints-panel'],
    props: {
        assetTypes: {
            type: Array,
            default: () => []
        },
        start_date: {
            type: [Date, String],
            default: null
        },
        end_date: {
            type: [Date, String],
            default: null
        },
        playbookAssets: {
            type: Array,
            default: () => []
        },
        concepts: {
            type: Array,
            default: () => []
        },
        assetTypeFilter: {
            type: Array,
            default: () => []
        },
        dropdownAssetTypeFilter: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            assetTypesWithLaunchDate: [],
            launchDay: null,
            startDay: null,
            initialStartDay: null, // Store the initial start day to prevent recalculation
            collapsedCards: [],
            miniMenuOpenFor: null,
            selectedAssetId: null,
            showModal: false,
            selectedAssetType: null,
            // showAssetTypeModal: false,
            eventDropDebounceTimer: null,
            pendingEventDrop: null,
            // Touchpoints panel state
            showTouchpointsPanel: false,
            isPanelOpen: false, // Tracks grid state for smoother animation
            touchpointsPanelDate: null,
            touchpointsPanelTouchpoints: [],
            panelCollapsedCards: [],
            panelMiniMenuOpenFor: null,
            panelSelectedAssetId: null,
            panelShowModal: false,
            panelSelectedAssetType: null,
            panelShowAssetTypeModal: false,
            calendarOptions: {
                plugins: [dayGridPlugin, interactionPlugin],
                initialView: 'dayGridMonth',
                headerToolbar: {
                    left: 'title',
                    center: '',
                    right: 'prev,today,next'
                },
                buttonText: {
                    today: 'Today'
                },
                editable: true,
                droppable: true,
                events: [],
                eventDrop: this.handleEventDrop,
                eventContent: this.renderEventContent,
                eventDidMount: this.handleEventDidMount,
                dayCellClassNames: this.getDayCellClassNames,
                eventClick: this.handleEventClick,
                datesSet: (dateInfo) => {
                    // Re-inject headers when view changes (month navigation)
                    this.$nextTick(() => {
                        this.injectCustomDayHeaders();
                        this.initializeMoreLinkTooltips();
                    });
                },
                height: 'auto',
                fixedWeekCount: false,
                showNonCurrentDates: false,
                dayMaxEvents: 3,
                moreLinkClick: (arg) => {
                    this.handleMoreLinkClick(arg);
                    return false; // Prevent default popover
                },
                moreLinkContent: this.renderMoreLinkContent,
                eventDisplay: 'block',
                displayEventTime: false,
                displayEventEnd: false,
            }
        };
    },
    setup() {
        const playbookStore = usePlaybookStore();
        const clientStore = useClientStore();
        const { showNotification } = useNotifications();
        return {
            playbookStore,
            clientStore,
            showNotification
        };
    },
    mounted() {
        this.initializeCalendar();
        // Navigate to the start_date month after calendar is initialized
        this.navigateToStartDate();
        // Inject custom day headers after calendar renders
        this.$nextTick(() => {
            this.injectCustomDayHeaders();
            this.initializeMoreLinkTooltips();
        });
    },
    computed: {
        client() {
            return this.clientStore.getClient;
        },
        playbook() {
            return this.playbookStore.getSelectedPlaybook;
        },
        selectedAssetForModal() {
            // Make this reactive to store updates by finding the asset from the store
            if (!this.selectedAssetId || !this.playbookStore.playbook_selected.assets) {
                return null;
            }
            return this.playbookStore.playbook_selected.assets.find(a => a.id === this.selectedAssetId) || null;
        },
        selectedAssetStatus() {
            if (!this.selectedAssetForModal) {
                return null;
            }
            return this.getTouchpointStatus(this.selectedAssetForModal);
        },
        // dropdownAssetTypeFilterKey() {
        //     // Create a reactive key based on dropdown filter to force legend re-render
        //     if (!this.dropdownAssetTypeFilter || !Array.isArray(this.dropdownAssetTypeFilter)) {
        //         return 'empty';
        //     }
        //     return this.dropdownAssetTypeFilter.sort().join(',');
        // }
    },
    methods: {
        // Navigate calendar to the start_date month
        navigateToStartDate() {
            this.$nextTick(() => {
                if (this.$refs.fullCalendar && this.startDay) {
                    const calendarApi = this.$refs.fullCalendar.getApi();
                    if (calendarApi) {
                        calendarApi.gotoDate(this.startDay);
                    }
                }
            });
        },

        // Inject custom day headers into FullCalendar DOM
        injectCustomDayHeaders() {
            if (!this.$refs.fullCalendar) return;
            
            const calendarEl = this.$refs.fullCalendar.$el;
            const toolbar = calendarEl.querySelector('.fc-header-toolbar');
            const viewHarness = calendarEl.querySelector('.fc-view-harness');
            
            if (!toolbar || !viewHarness) return;
            
            // Check if header already exists to avoid duplicates
            let existingHeader = calendarEl.querySelector('.custom-calendar-header');
            if (existingHeader) {
                existingHeader.remove();
            }
            
            // Create the custom header
            const headerDiv = document.createElement('div');
            headerDiv.className = 'custom-calendar-header';
            
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            dayNames.forEach(day => {
                const dayHeader = document.createElement('div');
                dayHeader.className = 'custom-day-header';
                dayHeader.textContent = day;
                headerDiv.appendChild(dayHeader);
            });
            
            // Insert between toolbar and view-harness
            toolbar.parentNode.insertBefore(headerDiv, viewHarness);
        },

        // Retrieve the last day of the first month when end_date is not set
        getLastDayOfFirstMonth() {
            const today = new Date();
            const firstMonth = new Date(today.getFullYear(), today.getMonth(), 28);
            return firstMonth;
        },

        // Check if a given date is the start date or end date (launch date)
        isLaunchDate(date) {
            if (!this.launchDay && !this.startDay) return false;
            const checkDate = new Date(date);
            
            // Check if it matches the end date (launchDay)
            const matchesEndDate = this.launchDay && (
                checkDate.getDate() === this.launchDay.getDate() &&
                checkDate.getMonth() === this.launchDay.getMonth() &&
                checkDate.getFullYear() === this.launchDay.getFullYear()
            );
            
            // Check if it matches the start date (startDay)
            const matchesStartDate = this.startDay && (
                checkDate.getDate() === this.startDay.getDate() &&
                checkDate.getMonth() === this.startDay.getMonth() &&
                checkDate.getFullYear() === this.startDay.getFullYear()
            );
            
            return matchesEndDate || matchesStartDate;
        },

        // Get CSS classes for day cells
        getDayCellClassNames(arg) {
            const classes = [];
            const checkDate = new Date(arg.date);
            
            // Check if it matches the end date (launchDay)
            if (this.launchDay && (
                checkDate.getDate() === this.launchDay.getDate() &&
                checkDate.getMonth() === this.launchDay.getMonth() &&
                checkDate.getFullYear() === this.launchDay.getFullYear()
            )) {
                classes.push('launch-date', 'end-date');
            }
            
            // Check if it matches the start date (startDay)
            if (this.startDay && (
                checkDate.getDate() === this.startDay.getDate() &&
                checkDate.getMonth() === this.startDay.getMonth() &&
                checkDate.getFullYear() === this.startDay.getFullYear()
            )) {
                classes.push('launch-date', 'start-date');
            }
            
            return classes;
        },

        // Initialize the calendar by setting up months and placing assets
        initializeCalendar() {
            // Filter assetTypes where has_launch_date is true
            this.assetTypesWithLaunchDate = this.assetTypes.filter(asset => asset.has_launch_date);

            // Get the max days_before_end_date in the assetTypesWithLaunchDate array
            const maxDaysFromEnd = this.assetTypesWithLaunchDate.length > 0 
                ? Math.max(...this.assetTypesWithLaunchDate.map(asset => asset.pivot.days_before_end_date))
                : 0;

            if (this.end_date) {
                // Parse end_date and extract UTC date components to avoid timezone conversion issues
                // Date strings like "2025-11-21" are interpreted as UTC midnight
                // We need to extract the UTC components and create a local date
                const endDateParsed = new Date(this.end_date);
                // Use UTC getters to get the actual date values from the string
                this.launchDay = new Date(
                    endDateParsed.getUTCFullYear(),
                    endDateParsed.getUTCMonth(),
                    endDateParsed.getUTCDate()
                );
            } else {
                if (maxDaysFromEnd <= 28) {
                    this.launchDay = this.getLastDayOfFirstMonth();
                } else {
                    // Use the last day of the second month (28th day)
                    const today = new Date();
                    this.launchDay = new Date(today.getFullYear(), today.getMonth() + 1, 28);
                }
            }

            // Use the actual playbook start_date if provided, otherwise calculate from maxDaysFromEnd
            // This should remain fixed and never be recalculated
            if (this.start_date) {
                // Parse start_date and extract UTC date components to avoid timezone conversion issues
                // Date strings like "2025-11-03" are interpreted as UTC midnight
                // We need to extract the UTC components and create a local date
                const startDateParsed = new Date(this.start_date);
                // Use UTC getters to get the actual date values from the string
                this.startDay = new Date(
                    startDateParsed.getUTCFullYear(),
                    startDateParsed.getUTCMonth(),
                    startDateParsed.getUTCDate()
                );
            } else if (this.launchDay && maxDaysFromEnd > 0) {
                // Fallback: Calculate the start date from end date and maxDaysFromEnd
                this.startDay = new Date(this.launchDay);
                this.startDay.setDate(this.launchDay.getDate() - maxDaysFromEnd);
            } else {
                this.startDay = this.launchDay ? new Date(this.launchDay) : null;
            }

            // Store the initial start day to prevent recalculation
            this.initialStartDay = this.startDay ? new Date(this.startDay) : null;

            // Set the initial date for the calendar to the start_date month
            this.calendarOptions.initialDate = this.startDay || this.launchDay || new Date();

            // Convert assets to FullCalendar events
            this.updateCalendarEvents();
        },

        // Get background color for an asset type based on its ID
        // getAssetTypeColor(assetTypeId) {
        //     // Color palette from _variables.scss (using 100-300 shades)
        //     // Excludes orange shades that match the end date (playbook-secondary: #FFCDAD, playbook-secondary-hover: #FFD4C4)
        //     // Excludes blue-passive (#EDF3FF) and blue-passive-hover (#DEEAFF) used for day cells with events
        //     // Excludes rgb(246, 245, 255) / #F6F5FF (purple-background)
        //     // Includes green, purple, blue, yellow, and pink/red color families
        //     const colors = [
        //         // Green shades
        //         '#ebffd0', 
        //         '#daf5b0',
        //         '#caea90',
        //         // Additional green shades
        //         '#EEFFD7',
        //         '#DFFFD7', 
        //         '#A7F8D1', 
        //         // Yellow/Orange-yellow shades (lighter than end date orange)
        //         '#FFECAE', 
        //         // Purple shades
        //         '#c3cdff', 
        //         // Blue shades (darker than blue-passive)
        //         '#9fadff',
        //         '#9ACEFD', 
        //         // Yellow shades
        //         '#fff2de',
        //         '#fee4b2', 
        //         '#fdd785', 
        //         // Pink/Red shades
        //         '#ffeded',
        //         '#ffc3ce',
        //         '#ff98af', 
        //     ];
            
        //     // Use modulo to cycle through colors if there are more asset types than colors
        //     return colors[assetTypeId % colors.length];
        // },

        // Get unique asset types that have launch dates
        // getUniqueAssetTypes() {
        //     const uniqueAssetTypes = [];
        //     const seenIds = new Set();
            
        //     this.assetTypesWithLaunchDate.forEach(assetType => {
        //         if (!seenIds.has(assetType.id)) {
        //             seenIds.add(assetType.id);
        //             uniqueAssetTypes.push(assetType);
        //         }
        //     });
            
        //     // Sort by name for consistent display
        //     return uniqueAssetTypes.sort((a, b) => {
        //         const nameA = a.name || 'Untitled';
        //         const nameB = b.name || 'Untitled';
        //         return nameA.localeCompare(nameB);
        //     });
        // },

        // Get asset color for an asset type object
        // getAssetColor(assetType) {
        //     return this.getAssetTypeColor(assetType.id);
        // },

        // Check if an asset type is filtered (should show strike-through)
        // Returns true when this asset type is NOT in the dropdown selection (meaning it's hidden)
        // This is a method so it can be called with different assetTypeIds in the template
        // isAssetTypeFiltered(assetTypeId) {
        //     // If dropdown filter is empty or not set, nothing is filtered (show all)
        //     if (!this.dropdownAssetTypeFilter || !Array.isArray(this.dropdownAssetTypeFilter) || this.dropdownAssetTypeFilter.length === 0) {
        //         return false;
        //     }
            
        //     // Check if this asset type ID is NOT in the dropdown selection
        //     // Convert both to strings for comparison to handle number/string mismatches
        //     const assetId = String(assetTypeId);
        //     const isInDropdownSelection = this.dropdownAssetTypeFilter.some(filterId => String(filterId) === assetId);
            
        //     // If it's not in the dropdown selection, it's filtered (should show strike-through)
        //     return !isInDropdownSelection;
        // },

        // Toggle asset type filter (syncs with dropdown filter)
        // toggleAssetTypeFilter(assetTypeId) {
        //     // Get current dropdown filter array
        //     const currentDropdownFilters = Array.isArray(this.dropdownAssetTypeFilter) ? [...this.dropdownAssetTypeFilter] : [];
        //     const clickedId = String(assetTypeId);
            
        //     // Check if this asset type is already in the dropdown selection
        //     const isCurrentlySelected = currentDropdownFilters.some(filterId => String(filterId) === clickedId);
            
        //     let newDropdownFilters;
        //     if (isCurrentlySelected) {
        //         // Remove from dropdown selection (filter it out)
        //         newDropdownFilters = currentDropdownFilters.filter(filterId => String(filterId) !== clickedId);
        //     } else {
        //         // Add to dropdown selection (show it)
        //         newDropdownFilters = [...currentDropdownFilters, assetTypeId];
        //     }
            
        //     // Emit event to update dropdown filter in parent
        //     this.$emit('update-dropdown-filter', newDropdownFilters);
        // },

        // Convert assets to FullCalendar event format
        updateCalendarEvents() {
            const events = [];

            // Process assets with launch dates from assetTypes
            this.assetTypesWithLaunchDate.forEach(asset => {
                const assetId = String(asset.id);
                
                // Apply dropdown filter: if set, only show events matching selected types
                if (Array.isArray(this.dropdownAssetTypeFilter) && this.dropdownAssetTypeFilter.length > 0) {
                    const isInDropdownFilter = this.dropdownAssetTypeFilter.some(filterId => String(filterId) === assetId);
                    if (!isInDropdownFilter) {
                        return; // Skip events that don't match dropdown filter
                    }
                }
                
                // Note: Legend filter is now synced with dropdown filter via isAssetTypeFiltered,
                // so we don't need separate legend filter logic here

                // Find the actual asset from playbookAssets to get the real launch_date
                const actualAsset = this.playbookAssets.find(a => a.id === asset.pivot.id);
                let assetDate;
                let hasLaunchDate = false;
                
                if (actualAsset && actualAsset.launch_date) {
                    hasLaunchDate = true;
                    // Use the actual launch_date from the asset
                    // Convert UTC date to local time for display
                    const utcDate = new Date(actualAsset.launch_date);
                    
                    // Extract LOCAL time components (not UTC) to create a Date object
                    // that FullCalendar will interpret as local time
                    assetDate = new Date(
                        utcDate.getFullYear(),      // Local year
                        utcDate.getMonth(),         // Local month
                        utcDate.getDate(),          // Local date
                        utcDate.getHours(),         // Local hours (already converted from UTC)
                        utcDate.getMinutes(),       // Local minutes
                        utcDate.getSeconds(),       // Local seconds
                        utcDate.getMilliseconds()   // Local milliseconds
                    );
                } else {
                    // Fall back to calculated date if launch_date doesn't exist
                    let daysFromEnd = asset.pivot.days_before_end_date;
                    assetDate = new Date(this.launchDay);
                    assetDate.setDate(this.launchDay.getDate() - daysFromEnd);
                    // Set a default time (00:00 AM) for display
                    assetDate.setHours(0, 0, 0, 0);
                }

                // Get background color based on asset_type.id
                // const backgroundColor = this.getAssetTypeColor(asset.id);

                // Create event object
                events.push({
                    id: asset.pivot.id.toString(),
                    title: asset.pivot.name || 'Untitled Touchpoint',
                    start: assetDate,
                    allDay: false, // Set to false to allow time display
                    editable: hasLaunchDate, // Only draggable if it has a launch_date
                    // backgroundColor: backgroundColor,
                    // borderColor: backgroundColor,
                    extendedProps: {
                        asset: actualAsset || asset,
                        assetTypeName: asset.name.endsWith('s') ? asset.name.slice(0, -1) : asset.name,
                        hasLaunchDate: hasLaunchDate
                    }
                });
            });

            // Process assets without launch dates from playbookAssets
            // Show them on the start date and make them non-draggable
            if (this.playbookAssets && Array.isArray(this.playbookAssets) && this.startDay) {
                this.playbookAssets.forEach(asset => {
                    // Skip if asset already has a launch_date (already processed above)
                    if (asset.launch_date) {
                        return;
                    }

                    // Skip if asset doesn't have an asset_type
                    if (!asset.asset_type) {
                        return;
                    }

                    const assetTypeId = String(asset.asset_type.id);
                    
                    // Apply dropdown filter: if set, only show events matching selected types
                    if (Array.isArray(this.dropdownAssetTypeFilter) && this.dropdownAssetTypeFilter.length > 0) {
                        const isInDropdownFilter = this.dropdownAssetTypeFilter.some(filterId => String(filterId) === assetTypeId);
                        if (!isInDropdownFilter) {
                            return; // Skip events that don't match dropdown filter
                        }
                    }

                    // Show on start date with default time (10:00 AM)
                    // Always use the initial start day to prevent it from changing when touchpoints are dragged
                    const startDate = this.initialStartDay ? new Date(this.initialStartDay) : (this.startDay ? new Date(this.startDay) : new Date());
                    startDate.setHours(10, 0, 0, 0);

                    // Get background color based on asset_type.id
                    // const backgroundColor = this.getAssetTypeColor(asset.asset_type.id);

                    // Create event object (non-draggable)
                    events.push({
                        id: asset.id.toString(),
                        title: asset.name || 'Untitled Touchpoint',
                        start: startDate,
                        allDay: false,
                        editable: false, // Not draggable - no launch_date
                        // backgroundColor: backgroundColor,
                        // borderColor: backgroundColor,
                        extendedProps: {
                            asset: asset,
                            assetTypeName: asset.asset_type.name.endsWith('s') ? asset.asset_type.name.slice(0, -1) : asset.asset_type.name,
                            hasLaunchDate: false
                        }
                    });
                });
            }

            this.calendarOptions.events = events;
        },

        // Handle event drop (drag and drop)
        handleEventDrop(info) {
            // Check if this event is draggable (has launch_date)
            const hasLaunchDate = info.event.extendedProps?.hasLaunchDate;
            if (!hasLaunchDate) {
                // Event doesn't have launch_date, so it's not draggable
                info.revert();
                return;
            }

            const newDate = info.event.start;
            
            // Use the event ID to find the correct asset
            // The event ID is set to asset.pivot.id (the playbook asset ID) in updateCalendarEvents
            const assetId = parseInt(info.event.id);
            const actualAsset = this.playbookAssets.find(a => a.id === assetId);
            
            if (!actualAsset) {
                console.error('Asset not found for event ID:', assetId);
                info.revert();
                return;
            }

            // Get client URL from window object
            const clientUrl = window.__CLIENT__?.url;
            
            if (!clientUrl) {
                console.error('Client URL not found');
                info.revert();
                return;
            }

            // Store the info object for potential revert
            this.pendingEventDrop = { info, asset: actualAsset, newDate };

            // Clear any existing debounce timer
            if (this.eventDropDebounceTimer) {
                clearTimeout(this.eventDropDebounceTimer);
            }

            // Debounce the save operation (500ms delay)
            this.eventDropDebounceTimer = setTimeout(() => {
                this.saveEventDrop();
            }, 500);
        },

        // Save the event drop (called after debounce)
        saveEventDrop() {
            if (!this.pendingEventDrop) {
                return;
            }

            const { info, asset, newDate } = this.pendingEventDrop;
            this.pendingEventDrop = null;

            // Get the original launch_date to preserve the time
            let originalLaunchDate;
            if (asset.launch_date) {
                // Parse the UTC launch_date to get the original time
                originalLaunchDate = new Date(asset.launch_date);
            } else {
                // Fallback: use current time if no launch_date exists
                originalLaunchDate = new Date();
                originalLaunchDate.setHours(10, 0, 0, 0); // Default to 10 AM
            }

            // Extract the date components from the new drop location (local time)
            const newDateLocal = new Date(newDate);
            const newYear = newDateLocal.getFullYear();
            const newMonth = newDateLocal.getMonth();
            const newDay = newDateLocal.getDate();

            // Extract the time components from the original launch_date (local time)
            const originalHours = originalLaunchDate.getHours();
            const originalMinutes = originalLaunchDate.getMinutes();
            const originalSeconds = originalLaunchDate.getSeconds();
            const originalMilliseconds = originalLaunchDate.getMilliseconds();

            // Create a new date with the new date but preserving the original time (in local timezone)
            // JavaScript Date constructor interprets these as local time components
            const newLaunchDateLocal = new Date(
                newYear,
                newMonth,
                newDay,
                originalHours,
                originalMinutes,
                originalSeconds,
                originalMilliseconds
            );

            // The Date object internally stores time as UTC (milliseconds since epoch)
            // When serialized with toISOString(), it will be in UTC format
            // No manual conversion needed - JavaScript handles it
            const newLaunchDateUTC = newLaunchDateLocal;

            // Calculate new days_before_end_date for reference
            const launchDate = new Date(this.launchDay);
            launchDate.setUTCHours(0, 0, 0, 0);
            const targetDate = new Date(newLaunchDateUTC);
            targetDate.setUTCHours(0, 0, 0, 0);
            const daysFromEnd = Math.floor((launchDate - targetDate) / (1000 * 3600 * 24));

            // Make API call to update the launch date
            // Include all existing asset values to prevent them from being reset
            const updatePayload = {
                launch_date: newLaunchDateUTC.toISOString(),
                name: asset.name || '',
                description: asset.description || null,
                status: asset.status || 'draft',
                inputs: asset.inputs || [],
                source: asset.source || null,
                source_details: asset.source_details || null
            };

            // Include segment if it exists
            if (asset.segment) {
                updatePayload.segment = asset.segment;
            }

            // Include destination_asset_id if it exists
            if (asset.destination_asset_id) {
                updatePayload.destination_asset_id = asset.destination_asset_id;
            }

            // Store asset reference to avoid closure issues
            const assetToUpdate = asset;
            
            window.axios.put(`/api/playbooks/${window.__CLIENT__?.url}/assets/${assetToUpdate.id}`, updatePayload)
            .then(response => {
                if (response.data && response.data.asset) {
                    // Update the local data
                    if (assetToUpdate.pivot) {
                        assetToUpdate.pivot.days_before_end_date = daysFromEnd;
                    }
                    
                    // Update the asset's launch_date
                    assetToUpdate.launch_date = newLaunchDateUTC.toISOString();
                    
                    // Emit success event to parent
                    this.$emit('update-days', {
                        assetId: assetToUpdate.id,
                        launchDate: newLaunchDateUTC.toISOString(),
                        daysBeforeEndDate: daysFromEnd
                    });
                    
                    // Refresh calendar events to show updated time
                    this.updateCalendarEvents();
                    
                    // Use the stored asset reference for notification
                    if (assetToUpdate) {
                        this.showNotification({
                            text: 'The launch date for <strong>' + assetToUpdate.name + '</strong> was updated!'
                        });
                    }
                }
            })
            .catch(error => {
                console.error('Error updating launch date:', error);
                
                // Revert the drag operation
                info.revert();
                
                // Show error toast
                if (window.Swal) {
                    window.Swal.fire({
                        title: 'Failed to update',
                        text: error.response?.data?.message || 'An error occurred while updating the launch date',
                        icon: 'error',
                        customClass: { popup: 'swal-error' }
                    });
                }
            });
        },

        // Custom event rendering
        renderEventContent(arg) {
            const assetTypeName = arg.event.extendedProps.assetTypeName;
            const title = arg.event.title || 'Untitled Touchpoint';
            const asset = arg.event.extendedProps.asset;
            const iconClass = asset?.asset_type?.icon_class || asset?.icon_class || 'circle';
            
            // Get the background color from the event
            const backgroundColor = arg.event.backgroundColor || '#f3f4f6';
            
            // Get the time from the event (default to 00:00 AM)
            const eventDate = arg.event.start;
            const hours = eventDate.getHours();
            const minutes = eventDate.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const displayTime = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
            
            // Create a custom element with icon and text
            const container = document.createElement('div');
            container.className = 'fc-event-main-frame';
            container.style.backgroundColor = backgroundColor;
            container.style.borderRadius = '20px';
            container.style.padding = '0px 8px';
            
            // Create icon element
            const iconWrapper = document.createElement('div');
            iconWrapper.className = 'fc-event-icon';
            const icon = document.createElement('img');
            icon.src = `/img/icons/avid-icons/${iconClass}.svg`;
            icon.className = 'icon';
            icon.onerror = function() {
                // Fallback to a default icon if the specific icon doesn't exist
                this.src = '/img/icons/avid-icons/circle.svg';
            };
            iconWrapper.appendChild(icon);
            
            // Create content wrapper
            const contentWrapper = document.createElement('div');
            contentWrapper.className = 'fc-event-content';
            
            // Create title
            const titleEl = document.createElement('div');
            titleEl.className = 'fc-event-title';
            titleEl.textContent = title;
            
            // Create time
            const timeEl = document.createElement('div');
            timeEl.className = 'fc-event-time';
            timeEl.textContent = displayTime;
            
            contentWrapper.appendChild(titleEl);
            contentWrapper.appendChild(timeEl);
            
            container.appendChild(iconWrapper);
            container.appendChild(contentWrapper);

            return { domNodes: [container] };
        },

        // Custom render for "more" link
        renderMoreLinkContent(arg) {
            const numEvents = arg.num;
            return `+${numEvents}`;
        },

        // Initialize tooltips on more-link buttons
        initializeMoreLinkTooltips() {
            if (!this.$refs.fullCalendar) return;
            
            const calendarEl = this.$refs.fullCalendar.$el;
            const moreLinks = calendarEl.querySelectorAll('.fc-daygrid-more-link');
            
            moreLinks.forEach(link => {
                // Destroy existing tooltip if any
                const existingTooltip = window.bootstrap?.Tooltip?.getInstance(link);
                if (existingTooltip) {
                    existingTooltip.dispose();
                }
                
                // Create new tooltip
                if (window.bootstrap?.Tooltip) {
                    new window.bootstrap.Tooltip(link, {
                        title: 'View all touchpoints',
                        placement: 'top',
                        trigger: 'hover'
                    });
                }
            });
        },

        // Update calendar size (called when side panel opens/closes)
        updateCalendarSize() {
            this.$nextTick(() => {
                if (this.$refs.fullCalendar) {
                    const calendarApi = this.$refs.fullCalendar.getApi();
                    if (calendarApi) {
                        calendarApi.updateSize();
                    }
                }
            });
        },

        // Handle more link click - show side panel
        handleMoreLinkClick(arg) {
            // Prevent default popover behavior
            if (arg.jsEvent) {
                arg.jsEvent.preventDefault();
                arg.jsEvent.stopPropagation();
            }
            
            // Hide any FullCalendar popovers that might have been created
            this.$nextTick(() => {
                const popover = document.querySelector('.fc-popover');
                if (popover) {
                    popover.style.display = 'none';
                }
            });
            
            // Get all events for this date (both visible and hidden)
            const date = arg.date;
            const allSegs = arg.allSegs || [];
            const allEvents = allSegs.map(seg => seg.event);
            
            // Map events to touchpoint data for the panel
            const touchpoints = allEvents.map(event => {
                const asset = event.extendedProps.asset;
                const iconClass = asset?.asset_type?.icon_class || asset?.icon_class || 'circle';
                const eventDate = event.start;
                const hours = eventDate.getHours();
                const minutes = eventDate.getMinutes();
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHours = hours % 12 || 12;
                const displayTime = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                
                return {
                    id: event.id,
                    title: event.title || 'Untitled Touchpoint',
                    time: displayTime,
                    assetTypeName: event.extendedProps.assetTypeName,
                    iconClass: iconClass,
                    asset: asset,
                    backgroundColor: event.backgroundColor
                };
            });
            
            // Open the panel directly
            this.handleOpenTouchpointsPanel({ date, touchpoints });
            
            // Return false to prevent default popover behavior
            return false;
        },

        // Handle event mount (add tooltip and cursor styling)
        handleEventDidMount(info) {
            // Get the touchpoint name from the event title
            const touchpointName = info.event.title || 'Untitled Touchpoint';
            
            // Find the event element (fc-event)
            const eventEl = info.el;
            
            // Add cursor styling for non-draggable events
            const hasLaunchDate = info.event.extendedProps?.hasLaunchDate;
            if (!hasLaunchDate) {
                // Add class and cursor style for non-draggable events
                eventEl.classList.add('fc-event-not-draggable');
                eventEl.style.cursor = 'not-allowed';
            }
            
            // Initialize Bootstrap tooltip on the event element
            if (eventEl && window.bootstrap && window.bootstrap.Tooltip) {
                // Destroy existing tooltip if any
                const existingTooltip = window.bootstrap.Tooltip.getInstance(eventEl);
                if (existingTooltip) {
                    existingTooltip.dispose();
                }
                
                // Create new tooltip
                new window.bootstrap.Tooltip(eventEl, {
                    title: touchpointName,
                    placement: 'top',
                    trigger: 'hover',
                    boundary: 'window'
                });
            }
        },

        // Handle event click (clicking on a touchpoint)
        handleEventClick(info) {
            // Get the asset ID from the event
            const assetId = parseInt(info.event.id);
            
            // Find the full asset object from playbookAssets
            const asset = this.playbookAssets.find(a => a.id === assetId);
            
            if (asset) {
                // Open the edit modal for this touchpoint
                this.editTouchpoint(asset);
            }
        },

        // Format event time
        formatEventTime(date) {
            if (!date) return '';
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        },

        // Format launch date and time
        formatLaunchDateTime(date) {
            if (!date) return '';
            const dateStr = this.utc_date(date);
            const timeStr = this.formatEventTime(date);
            return `${dateStr} ${timeStr}`;
        },

        toggleTouchpoint(touchpoint) {
            const index = this.collapsedCards.indexOf(touchpoint.id);
            if (index > -1) {
                this.collapsedCards.splice(index, 1);
            } else {
                this.collapsedCards.push(touchpoint.id);
            }
            this.$emit('cards-state-changed');
        },

        // viewAssetTypeInfo(touchpoint) {
        //     this.selectedAssetType = touchpoint.asset_type;
        //     this.showAssetTypeModal = true;
        // },

        openMiniMenu(touchpoint) {
            if (this.miniMenuOpenFor === touchpoint.id) {
                this.miniMenuOpenFor = null;
            } else {
                this.miniMenuOpenFor = touchpoint.id;
            }
        },
        
        closeMiniMenu() {
            this.miniMenuOpenFor = null;
        },
        
        async approveTouchpoint(touchpoint) {
            try {
                this.closeMiniMenu();
                
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}/approve`);
                
                if (response.data && response.data.asset) {
                    // Update the asset in the store
                    this.playbookStore.updateAsset(response.data.asset.id, response.data.asset);
                    
                    this.showNotification({
                        text: '<strong>' + response.data.asset.name + '</strong> was successfully updated!'
                    });
                }
            } catch (error) {
                console.error('Error approving touchpoint:', error);
                
                this.showNotification({
                    text: 'Failed to update <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },
        
        deployTouchpoint(touchpoint) {
            this.closeMiniMenu();
            // Reuse review event to trigger launch flow upstream with this single touchpoint
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        editTouchpoint(touchpoint) {
            this.selectedAssetId = touchpoint.id;
            this.showModal = true;
            this.closeMiniMenu();
        },
        
        async duplicateTouchpoint(touchpoint) {
            try {
                this.closeMiniMenu();
                
                // Create a copy of the touchpoint object
                const duplicatedAsset = {
                    ...touchpoint,
                    name: touchpoint.name + ' (Copy)',
                    id: null, // Will be set by backend
                    status: 'draft',
                    approved_by: null,
                    approved_at: null,
                    source: 'manual',
                    source_details: 'Duplicated from existing touchpoint'
                };
                
                // Optimistically add to store
                this.playbookStore.addAsset(duplicatedAsset);
                
                // Create the duplicate on the backend
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets`, {
                    client_id: this.client.id,
                    playbook_id: touchpoint.playbook_id,
                    playbook_asset_type_id: touchpoint.playbook_asset_type_id,
                    name: duplicatedAsset.name,
                    description: touchpoint.description,
                    launch_date: touchpoint.launch_date,
                    inputs: touchpoint.inputs,
                    status: 'draft',
                    segment: touchpoint.segment,
                    destination_asset_id: touchpoint.destination_asset_id
                });
                
                if (response.data && response.data.asset) {
                    // Remove optimistic asset with null ID if it exists
                    if (this.playbookStore.playbook_selected.assets) {
                        const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                        if (optimisticIndex >= 0) {
                            this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                        }
                    }
                    
                    // Add the actual asset data from backend
                    this.playbookStore.addAsset(response.data.asset);
                    
                    // Open the edit modal for the new asset
                    this.selectedAssetId = response.data.asset.id;
                    this.showModal = true;
                    
                    this.showNotification({
                        text: '<strong>' + response.data.asset.name + '</strong> was successfully created!'
                    });
                }
            } catch (error) {
                console.error('Error duplicating touchpoint:', error);
                
                // Remove the optimistically added asset on error (find by null ID and matching name)
                if (this.playbookStore.playbook_selected.assets) {
                    const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                    if (optimisticIndex >= 0) {
                        this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                    }
                }
                
                this.showNotification({
                    text: 'Failed to duplicate <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },
        
        reviewTouchpoint(touchpoint) {
            this.closeMiniMenu();
            
            // Emit event to parent to handle the review/export functionality
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        async deleteTouchpoint(touchpoint) {
            Swal.fire({
                title: `Are you sure you want to delete "${touchpoint.name}"?`,
                text: "This action cannot be undone.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await this.performDeleteTouchpoint(touchpoint);
                } else {
                    this.closeMiniMenu();
                }
            });
        },

        async performDeleteTouchpoint(touchpoint) {
            try {
                // Call the delete endpoint
                await window.axios.delete(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}`);

                // Remove from store
                this.playbookStore.removeAsset(touchpoint.id);

                // Close mini menu
                this.closeMiniMenu();

                Swal.fire({
                    title: "Deleted!",
                    text: "The touchpoint has been deleted.",
                    icon: "success",
                    customClass: {
                        popup: 'swal-success'
                    },
                });
            } catch (error) {
                console.error('Error deleting touchpoint:', error);
                
                this.showNotification({
                    text: 'Failed to delete <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
                throw error;
            }
        },

        async handleEditAsset(asset) {
            try {
                // Use the store method to update the asset
                const result = await this.playbookStore.updateAssetFromAPI(asset);

                if (result.success) {
                    this.$nextTick(()=>{
                        this.showNotification({
                            text: '<strong>' + asset.name + '</strong> was successfully updated!'
                        });
                    });
                }
            
                this.closeModal();
                
            } catch (error) {
                console.error('Error updating asset:', error);
                
                this.showNotification({
                    text: 'Failed to update <strong>' + asset.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },

        closeModal() {
            this.showModal = false;
            this.selectedAssetId = null;
        },

        // closeAssetTypeModal() {
        //     this.showAssetTypeModal = false;
        // },

        getPromptLabel(placeholder, assetType) {
            // Get the title from asset_type object, or use placeholder as fallback
            if (assetType && assetType.input_types) {
                const inputType = assetType.input_types.find(type => type.name === placeholder);
                if (inputType && inputType.title) {
                    return inputType.title;
                }
            }
            // Fallback to formatted placeholder
            return placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },

        formatPromptValue(value, placeholder) {
            // If it's a "link" type, return blank
            if (placeholder === 'link') {
                return '';
            }

            // If value is null or undefined, return empty string
            if (!value) {
                return '';
            }

            // Handle arrays - join with line breaks for visual separation
            if (Array.isArray(value)) {
                // Filter out empty values and join with line breaks
                const nonEmptyValues = value.filter(item => item !== null && item !== undefined && item !== '');
                if (nonEmptyValues.length === 0) {
                    return '';
                }
                
                // Format each value and join with double line breaks for clear separation
                const formattedValues = nonEmptyValues.map(item => {
                    let formattedItem = String(item);
                    // Replace h1-h6 tags with bold tags
                    formattedItem = formattedItem.replace(/<h[1-6][^>]*>/gi, '<strong>');
                    formattedItem = formattedItem.replace(/<\/h[1-6]>/gi, '</strong>');
                    return formattedItem;
                });
                
                // Join all values with double line breaks to create visual separation
                let formattedValue = formattedValues.join('<br><br>');
                
                // Count total lines across all values (including line breaks within each value)
                const totalLines = formattedValue.split(/<br\s*\/?>/i).length;
                if (totalLines > 4) {
                    // Split by <br> tags and limit to 4 lines
                    const parts = formattedValue.split(/<br\s*\/?>/i);
                    formattedValue = parts.slice(0, 4).join('<br>') + '...';
                }
                
                return formattedValue;
            }

            // Handle single values - convert to string and limit to 4 lines
            let formattedValue = String(value);
            
            // Replace h1-h6 tags with bold tags
            formattedValue = formattedValue.replace(/<h[1-6][^>]*>/gi, '<strong>');
            formattedValue = formattedValue.replace(/<\/h[1-6]>/gi, '</strong>');
            
            // Convert newlines to <br> tags for proper HTML rendering
            formattedValue = formattedValue.replace(/\n/g, '<br>');
            
            // Split into lines and limit to 4
            const lines = formattedValue.split(/<br\s*\/?>/i);
            if (lines.length > 4) {
                formattedValue = lines.slice(0, 4).join('<br>') + '...';
            }
            
            return formattedValue;
        },

        isDraftTouchpoint(touchpoint) {
            // A touchpoint is in draft mode if it's missing any required field
            if (!touchpoint || !touchpoint.asset_type || !touchpoint.asset_type.inputs || !touchpoint.inputs) {
                return true;
            }

            // Get required inputs (inputs where optional is false or not set)
            const requiredInputs = touchpoint.asset_type.inputs.filter(input => !input.optional);

            // Helper function to check if a value is empty
            const isEmpty = (value) => {
                if (value === undefined || value === null) return true;
                if (typeof value === 'string') return value.trim() === '';
                if (Array.isArray(value)) {
                    // Empty array is empty
                    if (value.length === 0) return true;
                    // Array with only null/undefined/empty values is considered empty
                    return value.every(item => {
                        if (item === null || item === undefined) return true;
                        if (typeof item === 'string') return item.trim() === '';
                        if (Array.isArray(item)) return isEmpty(item);
                        // For objects, check if they're empty (no meaningful content)
                        if (typeof item === 'object') {
                            // If it's an object, check if it has any non-empty properties
                            return Object.keys(item).length === 0 || 
                                   Object.values(item).every(val => isEmpty(val));
                        }
                        return false;
                    });
                }
                return false;
            };

            // Helper function to check group inputs
            const checkGroupInputs = (groupInput, groupItems) => {
                if (!groupItems || !Array.isArray(groupItems)) return true;

                // Get all required inputs for this group
                const groupRequiredInputs = touchpoint.asset_type.inputs.filter(
                    input => input.group_id === groupInput.id && !input.optional
                );

                // Check each item in the group
                for (const item of groupItems) {
                    // Skip null or undefined items
                    if (!item) continue;

                    for (const requiredInput of groupRequiredInputs) {
                        // Skip if requiredInput is null or doesn't have a placeholder
                        if (!requiredInput || !requiredInput.placeholder) continue;

                        // Safely access the value using the placeholder
                        const value = item[requiredInput.placeholder];
                        if (isEmpty(value)) {
                            return true; // Missing required field in group
                        }
                    }
                }

                return false; // All required fields in group are present
            };

            // Check each required input
            for (const requiredInput of requiredInputs) {
                // Skip if requiredInput is null or doesn't have a placeholder
                if (!requiredInput || !requiredInput.placeholder) continue;

                // Skip inputs that belong to a group (they're checked within their group)
                if (requiredInput.group_id) {
                    continue;
                }

                // Find the corresponding input value in touchpoint.inputs
                const input = Array.isArray(touchpoint.inputs) 
                    ? touchpoint.inputs.find(input => input && input.placeholder === requiredInput.placeholder)
                    : null;

                // If input is missing, it's a draft
                if (!input) {
                    // Exclude citations and link placeholders from required check
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true;
                    }
                    continue;
                }

                // Handle group inputs
                if (requiredInput.type === 'group') {
                    if (isEmpty(input.value) || checkGroupInputs(requiredInput, input.value)) {
                        return true; // Missing required group or required fields within group
                    }
                }
                // Handle regular inputs (not in a group)
                else if (isEmpty(input.value)) {
                    // Exclude citations and link placeholders from required check
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true; // Missing required field
                    }
                }
            }

            // All required fields are present
            return false;
        },

        getTouchpointStatus(touchpoint) {
            // Returns an object with label and CSS classes for the status badge
            if (touchpoint.source == null || this.isDraftTouchpoint(touchpoint)) {
                return { label: 'Incomplete', classes: 'bg-red bg-icon-warning' };
            } else if (touchpoint.deployed_at) {
                return { label: 'Launched', classes: 'bg-orange bg-icon-launched' };
            } else if (touchpoint.status == 'approved') {
                return { label: 'Approved', classes: 'bg-green bg-icon-ready' };
            } else if (touchpoint.status == 'generating') {
                return { label: 'Generating', classes: 'bg-blue bg-icon-generating' };
            } else {
                return { label: 'Ready', classes: 'bg-green bg-icon-ready' };
            }
        },

        // Touchpoints panel methods
        handleOpenTouchpointsPanel(data) {
            // Check if panel is already open for this same date
            if (this.showTouchpointsPanel && this.touchpointsPanelDate) {
                // Compare dates (year, month, day) to check if it's the same date
                const currentDate = new Date(this.touchpointsPanelDate);
                const clickedDate = new Date(data.date);
                const isSameDate = 
                    currentDate.getFullYear() === clickedDate.getFullYear() &&
                    currentDate.getMonth() === clickedDate.getMonth() &&
                    currentDate.getDate() === clickedDate.getDate();
                
                if (isSameDate) {
                    // Close the panel if it's already open for this date (toggle behavior)
                    this.handleCloseTouchpointsPanel();
                    return;
                }
            }
            
            this.touchpointsPanelDate = data.date;
            this.touchpointsPanelTouchpoints = data.touchpoints;
            
            // First update the grid layout
            this.isPanelOpen = true;
            
            // Then show the panel with animation after a tiny delay
            this.$nextTick(() => {
                this.showTouchpointsPanel = true;
                
                // Update calendar size and match panel height after panel opens
                this.$nextTick(() => {
                    this.updateCalendarSize();
                    // Small delay to ensure DOM is updated
                    setTimeout(() => {
                        this.matchPanelHeightToCalendar();
                    }, 100);
                });
            });
        },
        
        matchPanelHeightToCalendar() {
            this.$nextTick(() => {
                const calendarView = document.querySelector('.calendar-view');
                const sidePanel = document.querySelector('.touchpoints-side-panel');
                
                if (calendarView && sidePanel) {
                    const calendarHeight = calendarView.offsetHeight;
                    sidePanel.style.maxHeight = `${calendarHeight}px`;
                }
            });
        },
        
        handleCloseTouchpointsPanel() {
            this.showTouchpointsPanel = false;
            
            // Wait for panel exit animation to complete before updating grid
            setTimeout(() => {
                this.isPanelOpen = false;
                this.touchpointsPanelDate = null;
                this.touchpointsPanelTouchpoints = [];
                
                // Update calendar size after panel closes
                this.$nextTick(() => {
                    this.updateCalendarSize();
                });
            }, 300); // Match transition duration
        },
        
        formatPanelDate(date) {
            if (!date) return '';
            const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
            return date.toLocaleDateString('en-US', options);
        },

        // Panel card methods
        togglePanelTouchpoint(touchpoint) {
            const touchpointId = touchpoint.id;
            const index = this.panelCollapsedCards.indexOf(touchpointId);
            if (index > -1) {
                this.panelCollapsedCards.splice(index, 1);
            } else {
                this.panelCollapsedCards.push(touchpointId);
            }
        },
        
        openPanelMiniMenu(touchpoint) {
            const touchpointId = touchpoint.id;
            if (this.panelMiniMenuOpenFor === touchpointId) {
                this.panelMiniMenuOpenFor = null;
            } else {
                this.panelMiniMenuOpenFor = touchpointId;
            }
        },
        
        closePanelMiniMenu() {
            this.panelMiniMenuOpenFor = null;
        },
        
        viewAssetTypeInfoFromPanel(touchpoint) {
            this.panelSelectedAssetType = touchpoint.asset_type;
            this.panelShowAssetTypeModal = true;
        },
        
        closePanelAssetTypeModal() {
            this.panelShowAssetTypeModal = false;
        },
        
        editTouchpointFromPanel(touchpoint) {
            this.selectedAssetId = touchpoint.id;
            this.showModal = true;
            this.closePanelMiniMenu();
        },
        
        async approveTouchpointFromPanel(touchpoint) {
            try {
                this.closePanelMiniMenu();
                
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}/approve`);
                
                if (response.data && response.data.asset) {
                    this.playbookStore.updateAsset(response.data.asset.id, response.data.asset);

                    //Determine it is was approved or unapproved
                    const approvalStatus = response.data.asset.approved_at ? 'approved' : 'unapproved';
                    //Then use the touchpoint's name
                    const touchpointName = response.data.asset.name || 'Your touchpoint';
                    
                    this.showNotification({ 
                        text: '<strong>' + touchpointName + '</strong> was ' + approvalStatus + '!'
                    });
                }
            } catch (error) {
                console.error('Error approving touchpoint:', error);
                
                this.showNotification({
                    text: 'Failed to update approval status. Please try again.',
                    type: 'error'
                });
            }
        },
        
        deployTouchpointFromPanel(touchpoint) {
            this.closePanelMiniMenu();
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        async duplicateTouchpointFromPanel(touchpoint) {
            try {
                this.closePanelMiniMenu();
                
                const duplicatedAsset = {
                    ...touchpoint,
                    name: touchpoint.name + ' (Copy)',
                    id: null,
                    status: 'draft',
                    approved_by: null,
                    approved_at: null,
                    source: 'manual',
                    source_details: 'Duplicated from existing touchpoint'
                };
                
                this.playbookStore.addAsset(duplicatedAsset);
                
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets`, {
                    client_id: this.client.id,
                    playbook_id: touchpoint.playbook_id,
                    playbook_asset_type_id: touchpoint.playbook_asset_type_id,
                    name: duplicatedAsset.name,
                    description: touchpoint.description,
                    launch_date: touchpoint.launch_date,
                    inputs: touchpoint.inputs,
                    status: 'draft',
                    segment: touchpoint.segment,
                    destination_asset_id: touchpoint.destination_asset_id
                });
                
                if (response.data && response.data.asset) {
                    if (this.playbookStore.playbook_selected.assets) {
                        const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                        if (optimisticIndex >= 0) {
                            this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                        }
                    }
                    
                    this.playbookStore.addAsset(response.data.asset);
                    
                    this.selectedAssetId = response.data.asset.id;
                    this.showModal = true;
                    
                    this.showNotification({
                        text: '<strong>' + touchpoint.name + '</strong> was successfully duplicated!'
                    });
                }
            } catch (error) {
                console.error('Error duplicating touchpoint:', error);
                
                if (this.playbookStore.playbook_selected.assets) {
                    const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                    if (optimisticIndex >= 0) {
                        this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                    }
                }
                
                this.showNotification({
                    text: 'Failed to duplicate touchpoint. Please try again.',
                    type: 'error'
                });
            }
        },
        
        reviewTouchpointFromPanel(touchpoint) {
            this.closePanelMiniMenu();
            this.$emit('review-single-touchpoint', touchpoint);
        },
        
        async deleteTouchpointFromPanel(touchpoint) {
            Swal.fire({
                title: `Are you sure you want to delete "${touchpoint.name}"?`,
                text: "This action cannot be undone.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await window.axios.delete(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}`);
                        this.playbookStore.removeAsset(touchpoint.id);
                        
                        // Remove from panel touchpoints
                        this.touchpointsPanelTouchpoints = this.touchpointsPanelTouchpoints.filter(t => t.asset.id !== touchpoint.id);
                        
                        // Close panel if no touchpoints left
                        if (this.touchpointsPanelTouchpoints.length === 0) {
                            this.handleCloseTouchpointsPanel();
                        }

                        this.showNotification({
                            text: '<strong>' + touchpoint.name + '</strong> was successfully deleted!'
                        });
                    } catch (error) {
                        console.error('Error deleting touchpoint:', error);
                        
                        this.showNotification({
                            text: 'Failed to delete touchpoint. Please try again.',
                            type: 'error'
                        });
                    }
                } else {
                    this.closePanelMiniMenu();
                }
            });
        },

        // Refresh panel touchpoints when assets change
        refreshPanelTouchpoints() {
            if (!this.touchpointsPanelDate) return;
            
            // Get all events for the panel date
            const calendarApi = this.$refs.fullCalendar?.getApi();
            if (!calendarApi) return;
            
            const events = calendarApi.getEvents();
            const panelDateStr = this.touchpointsPanelDate.toISOString().split('T')[0];
            
            // Filter events for the panel date
            const dateEvents = events.filter(event => {
                const eventDateStr = event.start.toISOString().split('T')[0];
                return eventDateStr === panelDateStr;
            });
            
            // Map events to touchpoint data
            const touchpoints = dateEvents.map(event => {
                const asset = event.extendedProps.asset;
                const iconClass = asset?.asset_type?.icon_class || asset?.icon_class || 'circle';
                const eventDate = event.start;
                const hours = eventDate.getHours();
                const minutes = eventDate.getMinutes();
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHours = hours % 12 || 12;
                const displayTime = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                
                return {
                    id: event.id,
                    title: event.title || 'Untitled Touchpoint',
                    time: displayTime,
                    assetTypeName: event.extendedProps.assetTypeName,
                    iconClass: iconClass,
                    asset: asset,
                    backgroundColor: event.backgroundColor
                };
            });
            
            this.touchpointsPanelTouchpoints = touchpoints;
            
            // Close panel if no touchpoints left
            if (touchpoints.length === 0) {
                this.handleCloseTouchpointsPanel();
            }
        },

        // Handle launch date update from panel card
        async handlePanelLaunchDateUpdate(data) {
            const { touchpoint, newDate } = data;
            
            try {
                // Get client URL from window object
                const clientUrl = window.__CLIENT__?.url;
                
                if (!clientUrl) {
                    console.error('Client URL not found');
                    return;
                }

                // Prepare update payload
                const updatePayload = {
                    launch_date: newDate,
                    name: touchpoint.name || '',
                    description: touchpoint.description || null,
                    status: touchpoint.status || 'draft',
                    inputs: touchpoint.inputs || [],
                    source: touchpoint.source || null,
                    source_details: touchpoint.source_details || null
                };

                // Include segment if it exists
                if (touchpoint.segment) {
                    updatePayload.segment = touchpoint.segment;
                }

                // Include destination_asset_id if it exists
                if (touchpoint.destination_asset_id) {
                    updatePayload.destination_asset_id = touchpoint.destination_asset_id;
                }

                const response = await window.axios.put(`/api/playbooks/${clientUrl}/assets/${touchpoint.id}`, updatePayload);
                
                if (response.data && response.data.asset) {
                    // Update the asset in the store
                    this.playbookStore.updateAsset(touchpoint.id, response.data.asset);
                    
                    // Update the asset in the panel touchpoints array
                    const panelTouchpoint = this.touchpointsPanelTouchpoints.find(t => t.asset.id === touchpoint.id);
                    if (panelTouchpoint) {
                        panelTouchpoint.asset.launch_date = response.data.asset.launch_date;
                    }
                    
                    // Refresh calendar events to show updated date
                    this.updateCalendarEvents();
                    
                    // Refresh panel if it's still open
                    if (this.showTouchpointsPanel) {
                        this.refreshPanelTouchpoints();
                    }
                    
                    // Emit success event to parent
                    this.$emit('update-days', {
                        assetId: touchpoint.id,
                        launchDate: newDate,
                        daysBeforeEndDate: null // Will be recalculated by parent if needed
                    });
                    
                    // Show success toast
                    this.showNotification({
                        text: 'The launch date for <strong>' + touchpoint.name + '</strong> was successfully updated!'
                    });
                }
            } catch (error) {
                console.error('Error updating launch date:', error);
                
                this.showNotification({
                    text: 'Failed to update the launch date for <strong>' + touchpoint.name + '</strong>. Please try again.',
                    type: 'error'
                });
            }
        },
    },
    watch: {
        assetTypes: {
            handler() {
                this.initializeCalendar();
            },
            deep: true
        },
        start_date: {
            handler() {
                this.initializeCalendar();
                // Navigate to the start_date month when it changes
                this.navigateToStartDate();
            }
        },
        end_date: {
            handler() {
                this.initializeCalendar();
            }
        },
        assetTypeFilter: {
            handler() {
                // Update calendar events when legend filter changes
                this.updateCalendarEvents();
            }
        },
        dropdownAssetTypeFilter: {
            handler() {
                // Update calendar events when dropdown filter changes
                this.updateCalendarEvents();
            }
        },
        playbookAssets: {
            handler() {
                // Update calendar events when assets change (e.g., after drag and drop)
                this.updateCalendarEvents();
                // Refresh panel if it's open
                if (this.showTouchpointsPanel && this.touchpointsPanelDate) {
                    this.refreshPanelTouchpoints();
                }
            },
            deep: true
        }
    }
};
</script>